import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Preferences } from "@capacitor/preferences";
import { NativeBiometric } from "capacitor-native-biometric";
import useAuth from "../hooks/useAuth";
import useHaptics from "../hooks/useHaptics";
import { FaceIdIcon } from "../assets/icons";
import { IonButton } from "@ionic/react";
import { peekReturnTo } from "./returnTo";

export interface BiometricConfig {
  authTrigger: "always" | "on-login" | "after-timeout";
  timeoutMinutes: number;
  maxFailedAttempts: number;
  enabled: boolean;
  requireOnResume: boolean;
}

const DEFAULT_CONFIG: BiometricConfig = {
  authTrigger: "always",
  timeoutMinutes: 15,
  maxFailedAttempts: 10,
  enabled: false,
  requireOnResume: false,
};

const BIOMETRIC_ERROR_CODES: { [key: string]: string } = {
  "0": "Unknown authentication error",
  "1": "Biometric authentication is unavailable",
  "2": "User is locked out due to too many failed attempts",
  "3": "No biometric credentials are enrolled on this device",
  "4": "User is temporarily locked out",
  "10": "Authentication failed - biometric not recognized",
  "11": "Authentication canceled by application",
  "12": "Invalid authentication context",
  "13": "Device not in interactive mode",
  "14": "Device passcode is not set",
  "15": "Authentication canceled by system",
  "16": "Authentication canceled by user",
  "17": "User selected fallback authentication",
};

const AuthenticationModal: React.FC<{
  isVisible: boolean;
  onAuthenticate: () => void;
  onLogout: () => void;
  showLogout: boolean;
}> = ({ isVisible, onAuthenticate, onLogout, showLogout }) => {
  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[99999] bg-gray-800 bg-opacity-90 backdrop-blur-sm flex items-center justify-center">
      <div className="flex flex-col items-center gap-8">
        <div className="flex flex-col items-center">
          {/* <div className="text-center my-2">
            <div className="font-semibold text-lg">Tap to unlock</div>
          </div> */}
          <button
            onClick={onAuthenticate}
            className="w-32 h-32 flex items-center justify-center transition-colors"
            aria-label="Authenticate with biometrics"
            type="button"
          >
            <FaceIdIcon className="w-64 h-64" />
          </button>
        </div>

        {showLogout && (
          <IonButton
            onClick={onLogout}
            expand="block"
            className="primary-button"
          >
            Logout
          </IonButton>
        )}
      </div>
    </div>
  );
};

export const BiometricAuthHandler: React.FC = () => {
  const { isAuthenticated, isNativePlatform, logout } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);
  const [showLogoutButton, setShowLogoutButton] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [userBiometricEnabled, setUserBiometricEnabled] = useState<
    boolean | null
  >(null);
  const { successActivity, errorFeedback } = useHaptics();

  const hasCheckedBiometrics = useRef(false);
  const authInProgress = useRef(false);
  const isInitialized = useRef(false);
  const hasAuthenticatedThisSession = useRef(false);

  useEffect(() => {
    const initializeSettings = async () => {
      await Promise.all([
        loadConfiguration(),
        loadFailedAttempts(),
        loadUserBiometricPreference(),
      ]);
    };
    initializeSettings();
  }, []);

  useEffect(() => {
    if (userBiometricEnabled === null) return;

    if (!isInitialized.current) {
      isInitialized.current = true;
      if (
        isAuthenticated &&
        !location.pathname.includes("/signin") &&
        !location.pathname.includes("/verify")
      ) {
        checkBiometricStatus();
      }
      return;
    }

    if (
      isAuthenticated &&
      !location.pathname.includes("/signin") &&
      !location.pathname.includes("/verify") &&
      !hasCheckedBiometrics.current &&
      !authInProgress.current &&
      config.enabled &&
      userBiometricEnabled
    ) {
      checkBiometricStatus();
    }
  }, [
    isAuthenticated,
    location.pathname,
    config.enabled,
    userBiometricEnabled,
  ]);

  useEffect(() => {
    if (isAuthenticated && location.pathname === "/signin") {
      handlePostAuthRedirect();
    }
  }, [isAuthenticated, location.pathname]);

  useEffect(() => {
    if (!isAuthenticated) {
      hasCheckedBiometrics.current = false;
      authInProgress.current = false;
      hasAuthenticatedThisSession.current = false;
      resetFailedAttempts();
    }
  }, [isAuthenticated]);

  const loadConfiguration = async () => {
    try {
      const { value } = await Preferences.get({ key: "biometricConfig" });
      if (value) {
        const savedConfig = JSON.parse(value);
        setConfig({ ...DEFAULT_CONFIG, ...savedConfig });
      }
    } catch (error) {
      console.error("Error loading biometric configuration:", error);
    }
  };

  const loadUserBiometricPreference = async () => {
    try {
      const { value } = await Preferences.get({ key: "biometricEnabled" });
      setUserBiometricEnabled(
        value === null || value === undefined ? true : value === "true"
      );
    } catch (error) {
      console.error("Error loading user biometric preference:", error);
      setUserBiometricEnabled(true);
    }
  };

  const loadFailedAttempts = async () => {
    try {
      const { value } = await Preferences.get({
        key: "biometricFailedAttempts",
      });
      const attempts = value ? parseInt(value) : 0;
      setFailedAttempts(attempts);
      setShowLogoutButton(attempts >= config.maxFailedAttempts);
    } catch (error) {
      console.error("Error loading failed attempts:", error);
    }
  };

  const saveFailedAttempts = async (attempts: number) => {
    try {
      await Preferences.set({
        key: "biometricFailedAttempts",
        value: attempts.toString(),
      });
      setShowLogoutButton(attempts >= config.maxFailedAttempts);
    } catch (error) {
      console.error("Error saving failed attempts:", error);
    }
  };

  const resetFailedAttempts = async () => {
    try {
      setFailedAttempts(0);
      setShowLogoutButton(false);
      await Preferences.remove({ key: "biometricFailedAttempts" });
    } catch (error) {
      console.error("Error resetting failed attempts:", error);
    }
  };

  const checkBiometricStatus = async () => {
    try {
      if (config.enabled && userBiometricEnabled && isNativePlatform) {
        setShowModal(true);
        await checkBiometricAuth();
      } else {
        hasCheckedBiometrics.current = true;
      }
    } catch (error) {
      console.error("Error checking biometric status:", error);
      hasCheckedBiometrics.current = true;
    }
  };

  const checkBiometricAuth = async () => {
    if (authInProgress.current) return;

    try {
      authInProgress.current = true;

      const shouldAuthenticate = await shouldRequireAuth();

      if (shouldAuthenticate) {
        await performBiometricAuth();
      } else {
        hasCheckedBiometrics.current = true;
        setShowModal(false);
      }
    } catch (error) {
      console.error("Error in biometric check:", error);
      hasCheckedBiometrics.current = true;
      errorFeedback();
    } finally {
      authInProgress.current = false;
    }
  };

  const shouldRequireAuth = async (): Promise<boolean> => {
    switch (config.authTrigger) {
      case "always":
        return true;
      case "on-login":
        return !hasAuthenticatedThisSession.current;
      case "after-timeout": {
        const lastBioAuthResult = await Preferences.get({
          key: "lastBiometricAuth",
        });
        const lastAuthTimestamp = lastBioAuthResult.value
          ? parseInt(lastBioAuthResult.value)
          : 0;
        const currentTime = Date.now();
        const timeoutMs = config.timeoutMinutes * 60 * 1000;
        return currentTime - lastAuthTimestamp >= timeoutMs;
      }
      default:
        return false;
    }
  };

  const performBiometricAuth = async () => {
    try {
      await NativeBiometric.verifyIdentity({
        reason: "Please verify your identity to continue",
        title: "Authentication Required",
        subtitle: "Use biometric to continue",
        useFallback: true,
      });

      await recordSuccessfulAuth();
      await resetFailedAttempts();
      hasAuthenticatedThisSession.current = true;
      successActivity();
      hasCheckedBiometrics.current = true;
      setShowModal(false);
    } catch (error) {
      await handleAuthError(error);
      errorFeedback();
    }
  };

  const recordSuccessfulAuth = async () => {
    try {
      await Preferences.set({
        key: "lastBiometricAuth",
        value: Date.now().toString(),
      });
    } catch (error) {
      console.error("Error recording auth timestamp:", error);
    }
  };

  const handleAuthError = async (error: any) => {
    console.error("Biometric authentication error:", error);

    const errorCode = String(error?.code || "unknown");

    if (errorCode === "16" || errorCode === "10") {
      const newFailedAttempts = failedAttempts + 1;
      setFailedAttempts(newFailedAttempts);
      await saveFailedAttempts(newFailedAttempts);

      if (newFailedAttempts >= config.maxFailedAttempts) {
        setShowLogoutButton(true);
      }
    }
  };

  const handleLogout = async () => {
    try {
      await resetFailedAttempts();
      logout();
      setShowModal(false);
      history.replace("/signin");
    } catch (error) {
      console.error("Error during logout:", error);
      logout();
      history.replace("/signin");
    }
  };

  const handlePostAuthRedirect = async () => {
    // Safety net for "authenticated but still sitting on /signin". F-137: it used to send
    // everyone to /home, which overrode the router's own redirect to the parked deep link.
    // `peekReturnTo` (not `take`) — the sign-in handlers own spending it; this only mirrors
    // their destination so the two cannot disagree.
    const destination = peekReturnTo();
    try {
      history.replace(destination);
    } catch {
      history.replace(destination);
    }
  };

  return (
    <AuthenticationModal
      isVisible={showModal}
      onAuthenticate={checkBiometricAuth}
      onLogout={handleLogout}
      showLogout={showLogoutButton}
    />
  );
};

export const updateBiometricConfig = async (
  newConfig: Partial<BiometricConfig>
): Promise<void> => {
  try {
    const { value } = await Preferences.get({ key: "biometricConfig" });
    const currentConfig = value ? JSON.parse(value) : DEFAULT_CONFIG;
    const updatedConfig = { ...currentConfig, ...newConfig };

    await Preferences.set({
      key: "biometricConfig",
      value: JSON.stringify(updatedConfig),
    });
  } catch (error) {
    console.error("Error updating biometric configuration:", error);
    throw error;
  }
};

export const getBiometricConfig = async (): Promise<BiometricConfig> => {
  try {
    const { value } = await Preferences.get({ key: "biometricConfig" });
    return value ? JSON.parse(value) : DEFAULT_CONFIG;
  } catch (error) {
    console.error("Error getting biometric configuration:", error);
    return DEFAULT_CONFIG;
  }
};

export const resetAllBiometricSettings = async (): Promise<void> => {
  try {
    await Promise.all([
      Preferences.remove({ key: "biometricConfig" }),
      Preferences.remove({ key: "biometricEnabled" }),
      Preferences.remove({ key: "biometricFailedAttempts" }),
      Preferences.remove({ key: "lastBiometricAuth" }),
    ]);
  } catch (error) {
    console.error("Error resetting all biometric settings:", error);
    throw error;
  }
};
